import { getTimeStepsAroundDate } from "@sinch/utils/dateTime/getTimeStepsAroundDate";
import { roundDateMinutes } from "@sinch/utils/dateTime/interval";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { TimeInputBaseProps } from "./types";

export interface UseTimeInputProps
  extends Pick<TimeInputBaseProps, "minutesStep" | "onChange" | "optionsRangeInMinutes" | "referenceDate"> {
  value: DateTime;
  limitInclude?: "start" | "end" | "both" | "none";
}

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;
const UTC_ZONE = "utc";
const TIME_FORMAT = "HH:mm";

export const useDayTimeInput = ({
  value,
  minutesStep = 15,
  onChange,
  referenceDate,
  optionsRangeInMinutes,
  limitInclude,
}: UseTimeInputProps) => {
  const lastValidValue = useRef<DateTime | undefined>();

  useEffect(() => {
    if (value?.isValid) {
      lastValidValue.current = value;
    }
  }, [value]);

  const getReferenceDate = useCallback((): DateTime | undefined => {
    if (referenceDate) {
      return roundDateMinutes(minutesStep)(referenceDate);
    }
    if (value?.isValid) {
      return value;
    }
    return lastValidValue.current;
  }, [referenceDate, minutesStep, value]);

  const getTimeOffset = useCallback(
    (): number => (referenceDate ? roundDateMinutes(minutesStep)(referenceDate).toMillis() : 0),
    [referenceDate, minutesStep]
  );

  const getTimeRange = useCallback(() => {
    const timeOffset = getTimeOffset();
    const shiftByMinutes = referenceDate ? minutesStep : 0;
    return {
      minTime: DateTime.fromMillis(0).plus({
        millisecond: timeOffset,
        minutes: shiftByMinutes,
      }),
      maxTime: DateTime.fromMillis(MILLISECONDS_PER_DAY).plus({
        millisecond: timeOffset,
        minutes: shiftByMinutes,
      }),
    };
  }, [getTimeOffset, minutesStep, referenceDate]);

  const options = useMemo(() => {
    const currentReferenceDate = getReferenceDate();
    if (!currentReferenceDate) {
      return [];
    }

    const { minTime, maxTime } = getTimeRange();

    return getTimeStepsAroundDate(currentReferenceDate, {
      stepSizeInMinutes: minutesStep,
      minTime,
      maxTime,
      rangeSizeInMinutes: optionsRangeInMinutes,
      limitInclude,
    });
  }, [getReferenceDate, getTimeRange, minutesStep, optionsRangeInMinutes]);
  /**
   * Handles blur event for an input field.
   * If last valid value exists, it calls the `onChange` callback with the last valid value.
   */
  const handleBlur = useCallback(() => {
    if (lastValidValue.current && !value?.isValid) {
      const formattedValue = lastValidValue.current.setZone(UTC_ZONE).toFormat(TIME_FORMAT);
      onChange?.(formattedValue || null);
    }
  }, [onChange, value?.isValid]);

  /**
   * Handles the change event for a DateTime input. If date is invalid, it not calls the `onChange` callback.
   */
  const onChangeHandler = useCallback(
    (newValue: DateTime | null) => {
      if (newValue?.isValid) {
        const formattedValue = newValue.setZone(UTC_ZONE).toFormat(TIME_FORMAT);
        onChange?.(formattedValue);
        lastValidValue.current = undefined;
      } else if (value) {
        lastValidValue.current = value;
      }
    },
    [onChange, value]
  );

  return {
    options,
    onBlur: handleBlur,
    onChangeHandler,
  };
};
